import { AxiosRequestConfig } from 'axios';
import { logger } from '@nykaa/nes-logger';
import { getCacheInstance } from 'server/helpers/cacheInstance';
import { getRemoteConfigGivenCache } from 'server/helpers/remoteConfig';
import { DEFAULT_CONFIG } from 'store/remoteConfig/constants';
import { getSSRLanguage } from '../helpers/ssrLanguageContext';

const EXCLUDE_URL_ENV_KEYS = [
  'FOOTER_POPULAR_CONFIG_URL',
  'CONFIG_FLAGS_URL',
  // Add more env keys here as needed
];

const EXCLUDED_URLS = EXCLUDE_URL_ENV_KEYS.map(
  (key) => process.env[key]
).filter((url): url is string => !!url);

/**
 * Checks if a URL should be excluded from receiving custom headers.
 * Used by both SSR and CSR interceptors.
 */
export const isExcludedUrl = (url: string): boolean =>
  EXCLUDED_URLS.includes(url);

/**
 * Adds headers to an Axios request configuration object,
 * except for requests to exact URLs defined in EXCLUDED_URLS.
 * On server-side, if headers are not provided (undefined, null, or empty),
 * it automatically fetches headers from remote config.
 *
 * @param requestConfig - The Axios request configuration object.
 * @param headers - An object containing the headers to be added.
 * Optional on server-side.
 * @returns The modified Axios request configuration object.
 */
export const addRequestHeaders = (
  requestConfig: AxiosRequestConfig,
  headers?: { [key: string]: string } | null
) => {
  const url = requestConfig.url || '';
  const isExcluded = EXCLUDED_URLS.includes(url);

  if (__SERVER__) {
    logger.info(
      `[addRequestHeaders][SSR] URL: ${url}, ${
        isExcluded ? 'Skipping' : 'Adding'
      } headers`
    );
  }

  if (isExcluded) return requestConfig;

  let headersToApply = headers;

  if (__SERVER__ && !headersToApply) {
    try {
      const cache = getCacheInstance();
      if (cache) {
        const remoteConfig = getRemoteConfigGivenCache(cache);
        headersToApply =
          remoteConfig?.configs?.apiRequestHeaders ||
          DEFAULT_CONFIG.configs.apiRequestHeaders;
      } else {
        logger.warn(
          '[addRequestHeaders] Cache instance not available, using DEFAULT_CONFIG'
        );
        headersToApply = DEFAULT_CONFIG.configs.apiRequestHeaders;
      }
    } catch (error) {
      logger.warn(
        '[addRequestHeaders] Error fetching remote config, using DEFAULT_CONFIG',
        error
      );
      headersToApply = DEFAULT_CONFIG.configs.apiRequestHeaders;
    }
  }

  if (headersToApply && Object.keys(headersToApply).length) {
    requestConfig.headers = {
      ...(requestConfig.headers || {}),
      ...headersToApply,
    };
  }

  // Override X-NES-Language header with language from SSR context
  if (__SERVER__) {
    const ssrLang = getSSRLanguage();
    if (ssrLang && requestConfig.headers) {
      requestConfig.headers['X-NES-Language'] = ssrLang;
      logger.info(
        `[addRequestHeaders] Setting X-NES-Language from SSR context: ${ssrLang}`
      );
    }
  }

  return requestConfig;
};
